import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertCircle, Send, Loader2 } from "lucide-react";

/**
 * Phase 5 — SMTP Test-Email Card.
 * Lädt /api/email/status (SMTP-Diagnose ohne Passwort).
 * Erlaubt Admin-Rollen, eine Test-E-Mail via /api/email/test zu versenden.
 *
 * Props:
 *   variant: "tenant" | "agency" — beeinflusst nur die Akzentfarbe.
 */
export default function EmailTestCard({ variant = "tenant" }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/email/status");
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus({ enabled: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sendTest = async () => {
    if (!to.trim() || !to.includes("@")) { toast.error("Bitte gültige E-Mail eingeben"); return; }
    setSending(true);
    setLastResult(null);
    try {
      const r = await api.post("/email/test", { to: to.trim() });
      setLastResult(r.data);
      if (r.data.ok) toast.success(`Test-E-Mail gesendet an ${r.data.to}`);
      else toast.error(r.data.detail || "Versand fehlgeschlagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally { setSending(false); }
  };

  const accent = variant === "agency" ? "violet" : "orange";
  const accentBtn = variant === "agency" ? "bg-violet-600 hover:bg-violet-700" : "bg-orange-600 hover:bg-orange-700";
  const accentRing = variant === "agency" ? "border-violet-200" : "border-orange-200";

  if (loading) return null;

  const enabled = !!status?.enabled;

  return (
    <Card className={`p-6 space-y-4 ${accentRing}`} data-testid="email-test-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail className={`w-4 h-4 text-${accent}-600`} />
          <div>
            <h2 className="text-lg font-semibold">E-Mail-Versand testen</h2>
            <p className="text-xs text-slate-500">Verifiziert, dass die SMTP-Anbindung funktioniert.</p>
          </div>
        </div>
        <Badge
          data-testid="email-status-badge"
          className={enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}
        >
          {enabled ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
          {enabled ? "SMTP aktiv" : "SMTP inaktiv"}
        </Badge>
      </div>

      {enabled && status && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-600 bg-slate-50 rounded-md p-3 border border-slate-100" data-testid="email-status-details">
          <div><span className="text-slate-400">Server:</span> <code className="text-slate-700 font-mono">{status.host}:{status.port}</code></div>
          <div><span className="text-slate-400">Absender:</span> <code className="text-slate-700 font-mono">{status.from_address}</code></div>
          <div><span className="text-slate-400">TLS:</span> <code className="text-slate-700 font-mono">{status.tls_mode}</code></div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          data-testid="email-test-to"
          type="email"
          placeholder="empfaenger@example.de"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="flex-1 min-w-[220px]"
        />
        <Button
          data-testid="email-test-send-btn"
          onClick={sendTest}
          disabled={sending || !enabled}
          className={`text-white ${accentBtn}`}
        >
          {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sende …</>) : (<><Send className="w-4 h-4 mr-2" /> Test-E-Mail senden</>)}
        </Button>
      </div>

      {lastResult && (
        <div
          data-testid="email-test-result"
          className={`text-xs rounded-md p-3 border ${lastResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}
        >
          {lastResult.ok ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
          {lastResult.detail}
        </div>
      )}

      {!enabled && (
        <p className="text-xs text-slate-500">
          SMTP-Konfiguration fehlt. Bitte <code className="font-mono">SMTP_HOST</code>, <code className="font-mono">SMTP_USER</code>, <code className="font-mono">SMTP_PASSWORD</code> in den Backend-Variablen setzen und <code className="font-mono">SMTP_ENABLED=true</code> aktivieren.
        </p>
      )}
    </Card>
  );
}
